/**
 * MeetingQuickLinks - Configurable quick links sidebar for meeting dashboard
 * Displays notes, documents, and custom links defined in dashboard settings
 * Settings are managed from /governance/settings
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  FileText, FolderOpen, Link2, StickyNote,
  ExternalLink, ChevronDown, ChevronUp, Loader2, Settings
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../ui/button';

// Icon renderer helper
const IconRenderer = ({ name, className }) => {
  switch (name) {
    case 'FolderOpen':
      return <FolderOpen className={className} />;
    case 'Link2':
      return <Link2 className={className} />;
    case 'StickyNote':
      return <StickyNote className={className} />;
    case 'ExternalLink':
      return <ExternalLink className={className} />;
    case 'FileText':
    default:
      return <FileText className={className} />;
  }
};

// Quick Link Item Component
const QuickLinkItem = ({ item, onNavigate, onOpenDocument }) => {
  const [expanded, setExpanded] = useState(false);
  const noteHtml = React.useMemo(() => {
    const raw = item.content || '';
    const hasBlockHtml = /<(p|div|br|ul|ol|li|table|blockquote|h[1-6])\b/i.test(raw);
    if (hasBlockHtml) {
      return raw;
    }
    return raw.replace(/\n/g, '<br />');
  }, [item.content]);

  const handleClick = () => {
    if (item.type === 'note') {
      setExpanded(!expanded);
    } else if (item.type === 'link' && item.url) {
      if (item.url.startsWith('http')) {
        window.open(item.url, '_blank');
      } else {
        // Support governance deep-link patterns used in dashboard settings
        let target = item.url;
        if (target === '/governance?tab=documents') {
          target = '/governance/documents';
        }
        onNavigate(target);
      }
    } else if (item.type === 'document' && item.document_id) {
      if (onOpenDocument) {
        onOpenDocument(item.document_id, item.title);
      } else {
        onNavigate(`/governance/documents/${item.document_id}`);
      }
    }
  };

  if (item.type === 'divider') {
    return (
      <div className="py-2">
        <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">
          <IconRenderer name={item.icon} className="h-3 w-3" />
          {item.title}
        </div>
        <div className="mt-1 border-b border-gray-200" />
      </div>
    );
  }

  return (
    <div>
      <div 
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
        onClick={handleClick}
      >
        <IconRenderer name={item.icon} className="h-4 w-4 text-purple-600 flex-shrink-0" />
        <span className="text-sm text-gray-700 flex-1 truncate font-semibold">{item.title}</span>
        
        {item.type === 'note' && (
          <button className="text-gray-400 hover:text-gray-600">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        )}
        
        {item.type === 'link' && item.url?.startsWith('http') && (
          <ExternalLink className="h-3 w-3 text-gray-400" />
        )}
      </div>
      
      {/* Note content expansion */}
      {item.type === 'note' && expanded && (
        <div className="ml-6 mr-2 mb-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
          {item.content ? (
            <div
              className="prose prose-sm max-w-none text-gray-600 [&_ol]:list-decimal [&_ul]:list-disc [&_ol]:pl-5 [&_ul]:pl-5 [&_li]:my-0.5"
              dangerouslySetInnerHTML={{ __html: noteHtml }}
            />
          ) : (
            <span className="italic text-gray-400">No content</span>
          )}
        </div>
      )}
    </div>
  );
};

// Main Component
const MeetingQuickLinks = ({ 
  meetingId, 
  onNavigate,
  onOpenDocument,
  className = '',
  settingsEndpoint = '/board/dashboard-settings/default',
  settingsLink = '/governance/settings?tab=dashboard'
}) => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch dashboard settings
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(settingsEndpoint);
      setItems(response.data.items || []);
    } catch (err) {
      console.error('Failed to fetch dashboard settings:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Navigate handler
  const handleNavigate = (url) => {
    if (onNavigate) {
      onNavigate(url);
    } else {
      navigate(url);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-4 ${className}`}>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden flex flex-col ${className}`} data-testid="meeting-quick-links">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 text-sm flex items-center gap-2">
          <Link2 className="h-4 w-4 text-purple-600" />
          Pinned Resources
        </h3>
        {settingsLink && (
          <Button 
            variant="ghost" 
            size="sm" 
            className="h-7 w-7 p-0"
            onClick={() => navigate(settingsLink)}
            title="Configure pinned resources"
          >
            <Settings className="h-4 w-4 text-gray-500" />
          </Button>
        )}
      </div>

      {/* Items List */}
      <div className="p-2 flex-1 min-h-0 overflow-y-auto">
        {items.length === 0 ? (
          <div className="text-center py-6 text-gray-500">
            <Link2 className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No quick links configured</p>
            {settingsLink && (
              <>
                <p className="text-xs text-gray-400 mt-1">Configure in settings</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => navigate(settingsLink)}
                >
                  <Settings className="h-4 w-4 mr-1" />
                  Settings
                </Button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {items
              .sort((a, b) => (a.order || 0) - (b.order || 0))
              .map((item) => (
                <QuickLinkItem
                  key={item.id}
                  item={item}
                  onNavigate={handleNavigate}
                  onOpenDocument={onOpenDocument}
                />
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingQuickLinks;
